import { fabric } from 'fabric';
import { defaults } from 'lodash';
import { YObject } from 'src/app/sessions/common/y-session';
import { LINE_TYPE } from '../../context-menus/fabric-object-settings';
import { BoardItemConfig, CreationContext } from './board-item-configurations';

// Backward-compat types for line/arrow shapes
enum LegacyFabricLineType {
  LINE = 'line',
  ARROW = 'polygon',
}

export class LineConfigurations extends BoardItemConfig {
  startPoint: fabric.Point;
  endPoint: fabric.Point;
  type: LINE_TYPE;
  strokeWidth: number;
  strokeColor: string;

  static defaultConfigs = {
    startPoint: new fabric.Point(0, 0),
    endPoint: new fabric.Point(0, 0),
    type: LINE_TYPE.LINE,
    strokeWidth: 4,
    strokeColor: '#333333',
  };

  constructor(
    configurations: Partial<LineConfigurations> & Partial<BoardItemConfig>,
    globalCreationParams: CreationContext,
  ) {
    super(configurations, globalCreationParams);

    const lineConfigurations = defaults(configurations, LineConfigurations.defaultConfigs);
    this.startPoint = lineConfigurations.startPoint;
    this.endPoint = lineConfigurations.endPoint;
    this.type = lineConfigurations.type;
    this.strokeWidth = lineConfigurations.strokeWidth;
    this.strokeColor = lineConfigurations.strokeColor;
  }

  static fromYObject(object: YObject, globalCreationsParams: CreationContext): LineConfigurations {
    const configurations = BoardItemConfig.fromYObject(object, globalCreationsParams);
    if (object.type === LegacyFabricLineType.LINE) {
      // migrate old line shape using a temporary fabric.Line to compute absolute endpoints robustly
      const tempLine = this.createTempLineObject(object);
      const transformMatrix = tempLine.calcTransformMatrix();
      const absoluteStartPoint = fabric.util.transformPoint(
        new fabric.Point(tempLine.x1 ?? 0, tempLine.y1 ?? 0),
        transformMatrix,
      );
      const absoluteEndPoint = fabric.util.transformPoint(
        new fabric.Point(tempLine.x2 ?? 0, tempLine.y2 ?? 0),
        transformMatrix,
      );

      return new LineConfigurations(
        {
          ...configurations,
          angle: 0,
          scaleX: 1,
          scaleY: 1,
          left: tempLine.left,
          top: tempLine.top,
          startPoint: absoluteStartPoint,
          endPoint: absoluteEndPoint,
          type: LINE_TYPE.LINE,
          strokeWidth: tempLine.strokeWidth,
          strokeColor: tempLine.stroke,
        },
        globalCreationsParams,
      );
    } else if (object.type === LegacyFabricLineType.ARROW) {
      // migrate old arrow shape the same way as old line objects
      // from the polygon points array, the start point at index 0 and end point at index 3
      const tempArrow = this.createTempArrowObject(object);
      const transformMatrix = tempArrow.calcTransformMatrix();
      const startPoint = new fabric.Point(
        (tempArrow?.points?.[0].x ?? 0) - tempArrow.pathOffset.x,
        (tempArrow?.points?.[0].y ?? 0) - tempArrow.pathOffset.y,
      );
      const endPoint = new fabric.Point(
        (tempArrow?.points?.[3].x ?? 0) - tempArrow.pathOffset.x,
        (tempArrow?.points?.[3].y ?? 0) - tempArrow.pathOffset.y,
      );
      const absoluteStartPoint = fabric.util.transformPoint(startPoint, transformMatrix);
      const absoluteEndPoint = fabric.util.transformPoint(endPoint, transformMatrix);

      return new LineConfigurations(
        {
          ...configurations,
          angle: 0,
          scaleX: 1,
          scaleY: 1,
          startPoint: absoluteStartPoint,
          endPoint: absoluteEndPoint,
          type: LINE_TYPE.ARROW,
          strokeWidth: tempArrow.strokeWidth,
          strokeColor: tempArrow.stroke,
        },
        globalCreationsParams,
      );
    } else {
      // draw new board line shape
      return new LineConfigurations(
        {
          ...configurations,
          locked: (object as any).configurations.locked,
          startPoint: (object as any).configurations.startPoint,
          endPoint: (object as any).configurations.endPoint,
          type: (object as any).configurations.type,
          strokeWidth: (object as any).configurations.strokeWidth,
          strokeColor: (object as any).configurations.strokeColor,
        },
        globalCreationsParams,
      );
    }
  }

  private static createTempLineObject(o: any): fabric.Line {
    const isVertical = Math.abs(o?.x1 - o?.x2) < 1;
    const isHorizontal = Math.abs(o?.y1 - o?.y2) < 1;

    // set scaleX and scaleY to 1 if the line is vertical or horizontal explicitly
    // to avoid large scale values that was introduced from the old implementation
    return new fabric.Line([o?.x1 ?? 0, o?.y1 ?? 0, o?.x2 ?? 0, o?.y2 ?? 0], {
      left: o?.left ?? 0,
      top: o?.top ?? 0,
      scaleX: isVertical ? 1 : o?.scaleX ?? 1,
      scaleY: isHorizontal ? 1 : o?.scaleY ?? 1,
      angle: o?.angle ?? 0,
      flipX: o?.flipX ?? false,
      flipY: o?.flipY ?? false,
      originX: o?.originX ?? 'left',
      originY: o?.originY ?? 'top',
      stroke: o?.stroke,
      strokeWidth: o?.strokeWidth,
    });
  }

  private static createTempArrowObject(o: any): fabric.Polygon {
    return new fabric.Polygon(o?.points, {
      left: o?.left ?? 0,
      top: o?.top ?? 0,
      scaleX: o?.scaleX ?? 1,
      scaleY: o?.scaleY ?? 1,
      angle: o?.angle ?? 0,
      flipX: o?.flipX ?? false,
      flipY: o?.flipY ?? false,
      originX: o?.originX ?? 'left',
      originY: o?.originY ?? 'top',
      stroke: o?.stroke,
      strokeWidth: o?.strokeWidth,
    });
  }
}
